export const interpretationOfData = {
  title: "Interpretation of Data",
  description: "Analyze scientific data in tables, graphs, and diagrams. Recognize trends, translate data between formats, and perform interpolation or extrapolation.",
  percentage: "40-50%",
  color: "blue",
  skills: [
    {
      id: "reading-tables-graphs",
      title: "Reading Tables and Graphs",
      description: "Extract specific values and understand data organization in tables, graphs, and diagrams",
      difficulty: "Beginner",
      contentArea: "All Sciences",
      passages: [
        {
          id: "rtg-1",
          title: "Temperature vs. Solubility",
          context: "A student investigated how temperature affects the solubility of potassium nitrate (KNO₃) in water. The results are shown in Table 1.",
          data: "Table 1: Solubility of KNO₃\nTemperature (°C) | Solubility (g/100mL)\n20 | 31.6\n30 | 45.8\n40 | 63.9\n50 | 85.5\n60 | 110.0\n70 | 138.0",
          questions: [
            "What is the solubility of KNO₃ at 40°C?",
            "At what temperature does KNO₃ have a solubility of 110.0 g/100mL?",
            "How does solubility change as temperature increases?"
          ],
          solution: "1) At 40°C, the solubility is 63.9 g/100mL (read directly from table). 2) At 60°C, the solubility is 110.0 g/100mL. 3) As temperature increases, solubility increases consistently."
        },
        {
          id: "rtg-2",
          title: "Plant Growth Under Different Light Conditions",
          context: "Scientists measured the height of bean plants grown under different light wavelengths over 4 weeks.",
          data: "Figure 1: Plant Height (cm) vs. Time\nWeek | Red Light | Blue Light | Green Light | White Light\n1 | 2.5 | 2.3 | 1.8 | 2.6\n2 | 5.1 | 4.9 | 3.2 | 5.4\n3 | 8.2 | 7.8 | 4.5 | 8.9\n4 | 11.5 | 10.9 | 5.8 | 12.3",
          questions: [
            "Which light condition produced the tallest plants at Week 4?",
            "What was the height of plants under blue light at Week 2?",
            "Which light condition resulted in the slowest growth?"
          ],
          solution: "1) White light produced the tallest plants at 12.3 cm. 2) Blue light plants were 4.9 cm tall at Week 2. 3) Green light resulted in the slowest growth (only 5.8 cm by Week 4)."
        },
        {
          id: "rtg-3",
          title: "Atmospheric Pressure vs. Altitude",
          context: "A weather balloon recorded atmospheric pressure at various altitudes during ascent.",
          data: "Table 2: Pressure vs. Altitude\nAltitude (km) | Pressure (kPa)\n0 | 101.3\n2 | 79.5\n4 | 61.7\n6 | 47.2\n8 | 35.7\n10 | 26.5",
          questions: [
            "What is the atmospheric pressure at sea level (0 km)?",
            "At what altitude is the pressure 47.2 kPa?",
            "Describe the relationship between altitude and pressure."
          ],
          solution: "1) At sea level, pressure is 101.3 kPa. 2) At 6 km altitude, pressure is 47.2 kPa. 3) As altitude increases, atmospheric pressure decreases exponentially."
        },
        {
          id: "rtg-4",
          title: "Enzyme Activity vs. pH",
          context: "Researchers measured the activity of pepsin enzyme at different pH levels.",
          data: "Figure 2: Enzyme Activity\npH | Activity (units/min)\n1.0 | 45\n1.5 | 78\n2.0 | 95\n2.5 | 82\n3.0 | 58\n3.5 | 31\n4.0 | 12",
          questions: [
            "At what pH is pepsin activity highest?",
            "What is the enzyme activity at pH 3.0?",
            "What happens to activity as pH moves away from the optimum?"
          ],
          solution: "1) Pepsin activity is highest at pH 2.0 (95 units/min). 2) At pH 3.0, activity is 58 units/min. 3) Activity decreases as pH moves away from the optimum in either direction."
        },
        {
          id: "rtg-5",
          title: "Reaction Rate vs. Temperature",
          context: "A chemistry student measured how temperature affects the rate of a chemical reaction.",
          data: "Table 3: Temperature and Reaction Rate\nTemperature (°C) | Rate (mol/L·s)\n10 | 0.012\n20 | 0.028\n30 | 0.061\n40 | 0.135\n50 | 0.289\n60 | 0.612",
          questions: [
            "What is the reaction rate at 30°C?",
            "At what temperature is the rate 0.135 mol/L·s?",
            "How does temperature affect reaction rate?"
          ],
          solution: "1) At 30°C, the rate is 0.061 mol/L·s. 2) At 40°C, the rate is 0.135 mol/L·s. 3) As temperature increases, reaction rate increases exponentially (roughly doubles every 10°C)."
        },
        {
          id: "rtg-6",
          title: "Population Growth Over Time",
          context: "Ecologists tracked a rabbit population in a protected habitat over 8 years.",
          data: "Figure 3: Rabbit Population\nYear | Population\n0 | 50\n1 | 85\n2 | 142\n3 | 235\n4 | 380\n5 | 425\n6 | 445\n7 | 450\n8 | 448",
          questions: [
            "What was the population in Year 4?",
            "Between which years did the population grow most rapidly?",
            "What appears to happen after Year 5?"
          ],
          solution: "1) In Year 4, the population was 380 rabbits. 2) Growth was most rapid between Years 2-4 (exponential phase). 3) After Year 5, growth slows and population stabilizes around 450 (carrying capacity reached)."
        },
        {
          id: "rtg-7",
          title: "Density of Common Substances",
          context: "A student measured the density of various liquids and solids at room temperature.",
          data: "Table 4: Density Values\nSubstance | Density (g/cm³)\nCork | 0.24\nEthanol | 0.79\nWater | 1.00\nGlycerin | 1.26\nAluminum | 2.70\nIron | 7.87\nLead | 11.34",
          questions: [
            "Which substance has the lowest density?",
            "What is the density of water?",
            "Which substances would float in water?"
          ],
          solution: "1) Cork has the lowest density at 0.24 g/cm³. 2) Water has a density of 1.00 g/cm³. 3) Cork and ethanol would float in water (density < 1.00 g/cm³)."
        },
        {
          id: "rtg-8",
          title: "Photosynthesis Rate vs. Light Intensity",
          context: "Scientists measured oxygen production (indicator of photosynthesis) at different light intensities.",
          data: "Figure 4: O₂ Production Rate\nLight Intensity (lux) | O₂ (mL/hr)\n0 | 0\n500 | 2.3\n1000 | 4.8\n1500 | 7.1\n2000 | 8.9\n2500 | 9.8\n3000 | 10.0\n3500 | 10.0",
          questions: [
            "What is the O₂ production rate at 1500 lux?",
            "At what light intensity does the rate plateau?",
            "What is the maximum O₂ production rate observed?"
          ],
          solution: "1) At 1500 lux, O₂ production is 7.1 mL/hr. 2) The rate plateaus around 3000 lux. 3) Maximum O₂ production is 10.0 mL/hr (light saturation point reached)."
        },
        {
          id: "rtg-9",
          title: "Boiling Points of Alkanes",
          context: "A chemist recorded the boiling points of straight-chain alkanes with different numbers of carbon atoms.",
          data: "Table 5: Alkane Boiling Points\nAlkane | Carbon Atoms | Boiling Point (°C)\nMethane | 1 | -162\nEthane | 2 | -89\nPropane | 3 | -42\nButane | 4 | -1\nPentane | 5 | 36\nHexane | 6 | 69\nHeptane | 7 | 98\nOctane | 8 | 126",
          questions: [
            "What is the boiling point of pentane?",
            "Which alkane has a boiling point of 69°C?",
            "How does molecular size affect boiling point?"
          ],
          solution: "1) Pentane has a boiling point of 36°C. 2) Hexane has a boiling point of 69°C. 3) As the number of carbon atoms increases, boiling point increases due to stronger intermolecular forces."
        },
        {
          id: "rtg-10",
          title: "Earthquake Magnitude and Frequency",
          context: "Seismologists analyzed earthquake data from a tectonic plate boundary over 10 years.",
          data: "Figure 5: Annual Earthquake Count\nMagnitude Range | Number per Year\n2.0-2.9 | 8500\n3.0-3.9 | 1200\n4.0-4.9 | 180\n5.0-5.9 | 28\n6.0-6.9 | 4\n7.0-7.9 | 0.8\n8.0+ | 0.1",
          questions: [
            "How many magnitude 4.0-4.9 earthquakes occur annually?",
            "Which magnitude range is most common?",
            "What pattern exists between magnitude and frequency?"
          ],
          solution: "1) About 180 magnitude 4.0-4.9 earthquakes occur per year. 2) Magnitude 2.0-2.9 earthquakes are most common (8500/year). 3) As magnitude increases, frequency decreases exponentially."
        }
      ]
    },
    {
      id: "identifying-trends",
      title: "Identifying Trends and Patterns",
      description: "Recognize patterns, correlations, and relationships in scientific data",
      difficulty: "Beginner",
      contentArea: "All Sciences",
      passages: [
        {
          id: "itp-1",
          title: "Carbon Dioxide Levels Over Time",
          context: "Atmospheric CO₂ concentrations were measured at Mauna Loa Observatory from 1960 to 2020.",
          data: "Table 1: CO₂ Concentration (ppm)\nYear | CO₂ (ppm)\n1960 | 316.9\n1970 | 325.7\n1980 | 338.8\n1990 | 354.4\n2000 | 369.6\n2010 | 389.9\n2020 | 414.2",
          questions: [
            "What trend is observed in CO₂ levels over time?",
            "Is the rate of increase constant or changing?",
            "What was the approximate increase from 1960 to 2020?"
          ],
          solution: "1) CO₂ levels show a consistent upward trend. 2) The rate of increase is accelerating (larger jumps in recent decades). 3) CO₂ increased by approximately 97.3 ppm (from 316.9 to 414.2)."
        },
        {
          id: "itp-2",
          title: "Predator-Prey Population Cycles",
          context: "Ecologists tracked lynx and hare populations in a Canadian forest over 20 years.",
          data: "Figure 1: Population Dynamics\nYear | Hares (thousands) | Lynx (hundreds)\n0 | 15 | 2\n2 | 35 | 3\n4 | 65 | 8\n6 | 45 | 15\n8 | 20 | 12\n10 | 18 | 4\n12 | 40 | 3\n14 | 70 | 9\n16 | 50 | 16\n18 | 22 | 13\n20 | 16 | 5",
          questions: [
            "What pattern exists between hare and lynx populations?",
            "Which population peaks first?",
            "What is the approximate cycle length?"
          ],
          solution: "1) Populations show cyclical oscillations - when hares increase, lynx increase with a delay; when hares decrease, lynx decrease. 2) Hare population peaks first, followed by lynx. 3) The cycle length is approximately 10 years."
        },
        {
          id: "itp-3",
          title: "Melting Points of Period 3 Elements",
          context: "A chemistry class examined the melting points of elements across Period 3 of the periodic table.",
          data: "Table 2: Period 3 Melting Points\nElement | Atomic Number | Melting Point (°C)\nNa | 11 | 98\nMg | 12 | 650\nAl | 13 | 660\nSi | 14 | 1414\nP | 15 | 44\nS | 16 | 115\nCl | 17 | -102\nAr | 18 | -189",
          questions: [
            "What trend exists from Na to Si?",
            "What happens to melting point from P to Ar?",
            "Which element has the highest melting point?"
          ],
          solution: "1) From Na to Si, melting point increases (metallic bonding strengthens). 2) From P to Ar, melting point decreases (weaker molecular/atomic forces). 3) Silicon has the highest melting point at 1414°C (giant covalent structure)."
        },
        {
          id: "itp-4",
          title: "Velocity of a Falling Object",
          context: "A physics student dropped a ball and measured its velocity at 0.5-second intervals.",
          data: "Figure 2: Velocity vs. Time\nTime (s) | Velocity (m/s)\n0.0 | 0.0\n0.5 | 4.9\n1.0 | 9.8\n1.5 | 14.7\n2.0 | 19.6\n2.5 | 24.5\n3.0 | 29.4",
          questions: [
            "What pattern exists in the velocity data?",
            "What is the rate of velocity increase?",
            "What does this rate represent?"
          ],
          solution: "1) Velocity increases linearly with time. 2) Velocity increases by 9.8 m/s every second. 3) This rate is the acceleration due to gravity (g = 9.8 m/s²)."
        },
        {
          id: "itp-5",
          title: "Bacterial Growth Curve",
          context: "Microbiologists monitored bacterial colony growth in a nutrient-rich medium.",
          data: "Table 3: Bacterial Count\nTime (hours) | Cells (millions)\n0 | 0.1\n2 | 0.2\n4 | 0.8\n6 | 3.2\n8 | 12.8\n10 | 51.2\n12 | 95.0\n14 | 98.5\n16 | 99.0\n18 | 99.0",
          questions: [
            "What growth pattern occurs from 0-10 hours?",
            "What happens after 12 hours?",
            "Identify the three phases of growth."
          ],
          solution: "1) From 0-10 hours, exponential growth occurs (population doubles every 2 hours). 2) After 12 hours, growth slows and plateaus. 3) Three phases: lag phase (0-2h), exponential/log phase (2-12h), stationary phase (12-18h)."
        },
        {
          id: "itp-6",
          title: "Ionization Energy Across a Period",
          context: "Students analyzed first ionization energies of elements in Period 2.",
          data: "Figure 3: Ionization Energy (kJ/mol)\nElement | Li | Be | B | C | N | O | F | Ne\nIE₁ | 520 | 899 | 801 | 1086 | 1402 | 1314 | 1681 | 2081",
          questions: [
            "What is the general trend across the period?",
            "Which elements break the trend?",
            "Why does ionization energy generally increase?"
          ],
          solution: "1) Ionization energy generally increases from Li to Ne. 2) B and O show slight decreases compared to their predecessors. 3) IE increases because nuclear charge increases while shielding remains similar, making electrons harder to remove."
        },
        {
          id: "itp-7",
          title: "Tidal Heights Over 24 Hours",
          context: "Oceanographers recorded water level at a coastal station over one day.",
          data: "Table 4: Tide Height\nTime | Height (m)\n00:00 | 1.2\n03:00 | 3.8\n06:00 | 1.5\n09:00 | 3.6\n12:00 | 1.3\n15:00 | 3.9\n18:00 | 1.4\n21:00 | 3.7\n24:00 | 1.2",
          questions: [
            "What pattern exists in tidal heights?",
            "How many high tides occur in 24 hours?",
            "What is the approximate tidal period?"
          ],
          solution: "1) Tidal heights oscillate in a regular pattern between high and low tides. 2) Four high tides occur (at 03:00, 09:00, 15:00, 21:00). 3) The tidal period is approximately 6 hours (semidiurnal tides)."
        },
        {
          id: "itp-8",
          title: "Radioactive Decay of Carbon-14",
          context: "Scientists measured the remaining percentage of C-14 in a sample over time.",
          data: "Figure 4: C-14 Remaining\nTime (years) | % Remaining\n0 | 100\n5730 | 50\n11460 | 25\n17190 | 12.5\n22920 | 6.25\n28650 | 3.125",
          questions: [
            "What pattern exists in the decay data?",
            "What is the half-life of C-14?",
            "Is this linear or exponential decay?"
          ],
          solution: "1) The percentage remaining decreases by half at regular intervals. 2) The half-life is 5730 years (time for 50% decay). 3) This is exponential decay (constant half-life, not constant amount lost)."
        },
        {
          id: "itp-9",
          title: "Specific Heat Capacity of Metals",
          context: "A student measured how much energy was needed to raise the temperature of different metals by 10°C.",
          data: "Table 5: Energy Required (J/g)\nMetal | Specific Heat (J/g°C)\nLead | 0.13\nGold | 0.13\nSilver | 0.24\nCopper | 0.39\nIron | 0.45\nAluminum | 0.90\nWater | 4.18",
          questions: [
            "Which substance requires the most energy to heat?",
            "What pattern exists among metals?",
            "Why is water's value so different?"
          ],
          solution: "1) Water requires the most energy (4.18 J/g°C). 2) Denser metals generally have lower specific heat capacities. 3) Water's high specific heat is due to hydrogen bonding, which requires extra energy to break."
        },
        {
          id: "itp-10",
          title: "Species Diversity vs. Latitude",
          context: "Ecologists counted tree species in 1-hectare plots at different latitudes.",
          data: "Figure 5: Species Richness\nLatitude | Species Count\n0° (Equator) | 285\n10° | 240\n20° | 195\n30° | 145\n40° | 98\n50° | 62\n60° | 35\n70° | 18\n80° | 5",
          questions: [
            "What trend exists between latitude and diversity?",
            "Where is species richness highest?",
            "What might explain this pattern?"
          ],
          solution: "1) Species diversity decreases as latitude increases (moving toward poles). 2) Species richness is highest at the equator (285 species). 3) This pattern is due to more stable climate, higher productivity, and longer evolutionary time in tropical regions."
        }
      ]
    },
    {
      id: "interpolation-extrapolation",
      title: "Interpolation and Extrapolation",
      description: "Estimate values between data points (interpolation) and beyond the data range (extrapolation)",
      difficulty: "Intermediate",
      contentArea: "All Sciences",
      passages: [
        {
          id: "ie-1",
          title: "Protein Concentration Standard Curve",
          context: "A biochemist created a standard curve to determine unknown protein concentrations using absorbance measurements.",
          data: "Table 1: Standard Curve Data\nProtein (mg/mL) | Absorbance (595nm)\n0.0 | 0.00\n0.2 | 0.15\n0.4 | 0.31\n0.6 | 0.46\n0.8 | 0.62\n1.0 | 0.77",
          questions: [
            "Estimate the absorbance for 0.5 mg/mL protein (interpolation).",
            "If a sample has absorbance 0.85, estimate its protein concentration (extrapolation).",
            "Is the relationship linear?"
          ],
          solution: "1) At 0.5 mg/mL, absorbance would be approximately 0.385 (halfway between 0.31 and 0.46). 2) For absorbance 0.85, protein concentration would be approximately 1.1 mg/mL (extending the linear trend). 3) Yes, the relationship is linear (absorbance increases proportionally with concentration)."
        },
        {
          id: "ie-2",
          title: "Global Temperature Projections",
          context: "Climate scientists recorded average global temperature anomalies and created projections.",
          data: "Figure 1: Temperature Anomaly (°C)\nYear | Observed\n1980 | 0.26\n1990 | 0.45\n2000 | 0.61\n2010 | 0.92\n2020 | 1.25",
          questions: [
            "Estimate the temperature anomaly in 1995 (interpolation).",
            "Project the temperature anomaly for 2030 (extrapolation).",
            "What assumption does extrapolation require?"
          ],
          solution: "1) In 1995, the anomaly would be approximately 0.53°C (between 0.45 and 0.61). 2) For 2030, projecting the trend suggests approximately 1.6°C. 3) Extrapolation assumes the current trend continues, which may not account for policy changes or feedback loops."
        },
        {
          id: "ie-3",
          title: "Titration Curve Analysis",
          context: "A student performed an acid-base titration and recorded pH values as base was added.",
          data: "Table 2: Titration Data\nVolume NaOH (mL) | pH\n0 | 2.0\n5 | 3.2\n10 | 4.1\n15 | 4.8\n20 | 5.5\n25 | 10.5\n30 | 11.8",
          questions: [
            "Estimate the pH at 12.5 mL NaOH (interpolation).",
            "At what volume would pH reach 6.0 (interpolation)?",
            "Why is extrapolation beyond 30 mL unreliable?"
          ],
          solution: "1) At 12.5 mL, pH would be approximately 4.45 (between 4.1 and 4.8). 2) pH 6.0 would occur around 21-22 mL (just past the equivalence point). 3) Extrapolation is unreliable because pH plateaus after equivalence point; the curve is non-linear."
        },
        {
          id: "ie-4",
          title: "Projectile Motion Trajectory",
          context: "A physics class launched a ball at 45° and recorded its height at different horizontal distances.",
          data: "Figure 2: Projectile Path\nHorizontal Distance (m) | Height (m)\n0 | 0\n5 | 4.4\n10 | 7.5\n15 | 9.4\n20 | 10.0\n25 | 9.4\n30 | 7.5\n35 | 4.4\n40 | 0",
          questions: [
            "Estimate the height at 17.5 m horizontal distance (interpolation).",
            "Would the ball reach 50 m horizontal distance (extrapolation)?",
            "What is the maximum height?"
          ],
          solution: "1) At 17.5 m, height would be approximately 9.7 m (between 9.4 and 10.0). 2) No, extrapolating shows the ball lands at 40 m; it won't reach 50 m. 3) Maximum height is 10.0 m at 20 m horizontal distance (apex of parabola)."
        },
        {
          id: "ie-5",
          title: "Enzyme Kinetics - Michaelis-Menten",
          context: "Researchers measured reaction velocity at different substrate concentrations for an enzyme.",
          data: "Table 3: Enzyme Activity\nSubstrate [mM] | Velocity (μmol/min)\n0.5 | 12\n1.0 | 20\n2.0 | 30\n4.0 | 40\n8.0 | 48\n16.0 | 53\n32.0 | 56",
          questions: [
            "Estimate velocity at 3.0 mM substrate (interpolation).",
            "What would velocity be at 64.0 mM (extrapolation)?",
            "What is the approximate Vmax?"
          ],
          solution: "1) At 3.0 mM, velocity would be approximately 35 μmol/min (between 30 and 40). 2) At 64.0 mM, velocity would approach 57-58 μmol/min (curve is plateauing). 3) Vmax is approximately 58-60 μmol/min (maximum velocity as substrate approaches infinity)."
        },
        {
          id: "ie-6",
          title: "Cooling Curve of Hot Water",
          context: "A student measured the temperature of hot water as it cooled in a room at 20°C.",
          data: "Figure 3: Temperature vs. Time\nTime (min) | Temperature (°C)\n0 | 95\n5 | 72\n10 | 56\n15 | 45\n20 | 37\n25 | 31\n30 | 27",
          questions: [
            "Estimate temperature at 7.5 minutes (interpolation).",
            "What would temperature be at 40 minutes (extrapolation)?",
            "What temperature will the water approach?"
          ],
          solution: "1) At 7.5 minutes, temperature would be approximately 64°C (between 72 and 56). 2) At 40 minutes, temperature would be approximately 23-24°C (approaching room temperature). 3) The water will approach 20°C (room temperature) asymptotically."
        },
        {
          id: "ie-7",
          title: "Stellar Luminosity vs. Temperature",
          context: "Astronomers plotted luminosity against surface temperature for main sequence stars.",
          data: "Table 4: Main Sequence Stars\nTemperature (K) | Luminosity (Solar Units)\n3000 | 0.001\n4000 | 0.01\n5000 | 0.1\n6000 | 1.0\n7000 | 5.0\n8000 | 20.0\n9000 | 60.0",
          questions: [
            "Estimate luminosity at 5500 K (interpolation).",
            "What would luminosity be at 10000 K (extrapolation)?",
            "Is this relationship linear?"
          ],
          solution: "1) At 5500 K, luminosity would be approximately 0.3 solar units (between 0.1 and 1.0). 2) At 10000 K, luminosity would be approximately 150-200 solar units (continuing exponential trend). 3) No, this is an exponential relationship (luminosity ∝ T⁴)."
        },
        {
          id: "ie-8",
          title: "Population Growth Projection",
          context: "Census data for a city was collected every 10 years.",
          data: "Figure 4: City Population\nYear | Population\n1980 | 125000\n1990 | 148000\n2000 | 175000\n2010 | 207000\n2020 | 245000",
          questions: [
            "Estimate population in 2005 (interpolation).",
            "Project population for 2030 (extrapolation).",
            "What factors could affect accuracy?"
          ],
          solution: "1) In 2005, population would be approximately 191000 (halfway between 175000 and 207000). 2) For 2030, projecting the trend suggests approximately 290000. 3) Accuracy depends on continued growth rate; economic changes, migration, or policy could alter the trend."
        },
        {
          id: "ie-9",
          title: "Vapor Pressure of Water",
          context: "A chemistry lab measured the vapor pressure of water at various temperatures.",
          data: "Table 5: Vapor Pressure\nTemperature (°C) | Pressure (kPa)\n20 | 2.3\n30 | 4.2\n40 | 7.4\n50 | 12.3\n60 | 19.9\n70 | 31.2\n80 | 47.4",
          questions: [
            "Estimate vapor pressure at 55°C (interpolation).",
            "What would pressure be at 90°C (extrapolation)?",
            "At what temperature does water boil at standard pressure (101.3 kPa)?"
          ],
          solution: "1) At 55°C, vapor pressure would be approximately 15.5 kPa (between 12.3 and 19.9). 2) At 90°C, pressure would be approximately 70 kPa (continuing exponential trend). 3) Water boils at 100°C when vapor pressure equals atmospheric pressure (101.3 kPa)."
        },
        {
          id: "ie-10",
          title: "Glacier Retreat Measurements",
          context: "Glaciologists measured the terminus position of a glacier over several decades.",
          data: "Figure 5: Glacier Length (km from reference point)\nYear | Distance\n1970 | 12.5\n1980 | 12.1\n1990 | 11.5\n2000 | 10.7\n2010 | 9.6\n2020 | 8.2",
          questions: [
            "Estimate glacier position in 1995 (interpolation).",
            "Project position for 2030 (extrapolation).",
            "What is the average retreat rate?"
          ],
          solution: "1) In 1995, the glacier would be at approximately 11.1 km (between 11.5 and 10.7). 2) For 2030, projecting suggests approximately 6.8 km (continuing retreat trend). 3) Average retreat rate is approximately 0.086 km/year or 86 meters/year over 50 years."
        }
      ]
    },
    {
      id: "translating-data-formats",
      title: "Translating Between Data Formats",
      description: "Convert data between tables, graphs, diagrams, and written descriptions",
      difficulty: "Intermediate",
      contentArea: "All Sciences",
      passages: [
        {
          id: "tdf-1",
          title: "Phase Diagram of Water",
          context: "A phase diagram shows the states of water at different temperatures and pressures.",
          data: "Description: At 1 atm pressure and 0°C, ice melts to liquid water. At 1 atm and 100°C, liquid water boils to steam. The triple point occurs at 0.01°C and 0.006 atm where all three phases coexist. The critical point is at 374°C and 218 atm.",
          questions: [
            "What phase exists at 50°C and 1 atm?",
            "Describe what happens when pressure increases at -10°C.",
            "Convert this description to identify phase boundaries."
          ],
          solution: "1) At 50°C and 1 atm, water exists as a liquid (between melting and boiling points). 2) At -10°C, increasing pressure can cause ice to melt (crossing solid-liquid boundary). 3) Phase boundaries: solid-liquid at 0°C/1atm, liquid-gas at 100°C/1atm, triple point at 0.01°C/0.006atm."
        },
        {
          id: "tdf-2",
          title: "Photosynthesis Rate Data",
          context: "A graph shows photosynthesis rate (y-axis, 0-10 μmol CO₂/m²/s) versus light intensity (x-axis, 0-2000 μmol photons/m²/s). The curve starts at 0, rises steeply until 800, then gradually levels off, reaching a plateau at 9.5 around 1500.",
          data: "Graph description provided above.",
          questions: [
            "Create a table with 5 data points from this graph.",
            "At what light intensity is the rate 5 μmol CO₂/m²/s?",
            "Describe the relationship in words."
          ],
          solution: "1) Table: Light 0→Rate 0; Light 400→Rate 4; Light 800→Rate 7; Light 1200→Rate 8.5; Light 1600→Rate 9.5. 2) Rate of 5 μmol CO₂/m²/s occurs at approximately 500 μmol photons/m²/s. 3) Photosynthesis rate increases rapidly with light intensity initially, then plateaus as other factors become limiting (light saturation)."
        },
        {
          id: "tdf-3",
          title: "Periodic Trends Description",
          context: "Atomic radius decreases from left to right across a period due to increasing nuclear charge pulling electrons closer. Atomic radius increases down a group as additional electron shells are added.",
          data: "Description provided above.",
          questions: [
            "Which element is larger: Na or Cl?",
            "Which element is larger: Li or Cs?",
            "Create a simple table showing relative sizes for Li, Na, K."
          ],
          solution: "1) Na is larger than Cl (Na is left of Cl in Period 3). 2) Cs is larger than Li (Cs is below Li in Group 1). 3) Table: Li=smallest, Na=medium, K=largest (increasing down the group)."
        },
        {
          id: "tdf-4",
          title: "Velocity-Time Graph",
          context: "A car's velocity-time graph shows: 0-5s constant acceleration from 0 to 20 m/s (straight line), 5-15s constant velocity at 20 m/s (horizontal line), 15-20s constant deceleration from 20 to 0 m/s (straight line down).",
          data: "Graph description provided above.",
          questions: [
            "Create a table of velocity at 0, 5, 10, 15, 20 seconds.",
            "Calculate the acceleration during 0-5s.",
            "Describe the motion in words."
          ],
          solution: "1) Table: 0s→0m/s, 5s→20m/s, 10s→20m/s, 15s→20m/s, 20s→0m/s. 2) Acceleration = (20-0)/(5-0) = 4 m/s². 3) The car accelerates uniformly for 5s, travels at constant speed for 10s, then decelerates uniformly to a stop over 5s."
        },
        {
          id: "tdf-5",
          title: "Food Web Relationships",
          context: "In a grassland ecosystem: Grass is eaten by grasshoppers and rabbits. Grasshoppers are eaten by birds and frogs. Rabbits are eaten by foxes and hawks. Birds are eaten by hawks. Frogs are eaten by snakes. Snakes are eaten by hawks.",
          data: "Description provided above.",
          questions: [
            "Draw a simple food web diagram (describe connections).",
            "Which organism is the top predator?",
            "Create a table showing what each predator eats."
          ],
          solution: "1) Diagram: Grass→Grasshoppers→Birds→Hawks; Grass→Rabbits→Foxes; Grasshoppers→Frogs→Snakes→Hawks. 2) Hawks are the top predator (not eaten by anything). 3) Table: Birds eat grasshoppers; Frogs eat grasshoppers; Snakes eat frogs; Foxes eat rabbits; Hawks eat birds, rabbits, and snakes."
        },
        {
          id: "tdf-6",
          title: "Solubility Curve Description",
          context: "Potassium nitrate solubility increases dramatically with temperature: at 0°C, 13g dissolves per 100mL; at 20°C, 32g; at 40°C, 64g; at 60°C, 110g; at 80°C, 169g; at 100°C, 246g.",
          data: "Description provided above.",
          questions: [
            "Create a table from this description.",
            "Graph this data (describe the curve shape).",
            "Is this relationship linear or exponential?"
          ],
          solution: "1) Table created with Temperature (°C) and Solubility (g/100mL) columns as given. 2) Graph would show an upward curving line (exponential growth pattern). 3) This is an exponential relationship (solubility roughly doubles every 20°C)."
        },
        {
          id: "tdf-7",
          title: "Punnett Square Results",
          context: "A table shows a genetic cross: Top row shows R and r alleles from one parent. Left column shows R and r from other parent. Cells show: RR (top-left), Rr (top-right), Rr (bottom-left), rr (bottom-right).",
          data: "Table description provided above.",
          questions: [
            "What are the genotype ratios?",
            "If R is dominant red and r is recessive white, what are phenotype ratios?",
            "Describe this cross in words."
          ],
          solution: "1) Genotype ratios: 1 RR : 2 Rr : 1 rr (or 25% : 50% : 25%). 2) Phenotype ratios: 3 red : 1 white (75% red, 25% white). 3) This is a heterozygous cross (Rr × Rr) producing offspring with 3:1 phenotype ratio."
        },
        {
          id: "tdf-8",
          title: "Electromagnetic Spectrum",
          context: "A diagram shows wavelength ranges: Radio waves (>1m), Microwaves (1mm-1m), Infrared (700nm-1mm), Visible (400-700nm), Ultraviolet (10-400nm), X-rays (0.01-10nm), Gamma rays (<0.01nm). Energy increases as wavelength decreases.",
          data: "Diagram description provided above.",
          questions: [
            "Create a table ordering these by increasing energy.",
            "Which has longer wavelength: UV or infrared?",
            "Describe the relationship between wavelength and energy."
          ],
          solution: "1) Table (increasing energy): Radio, Microwave, Infrared, Visible, UV, X-ray, Gamma. 2) Infrared has longer wavelength than UV (700nm-1mm vs 10-400nm). 3) Energy and wavelength are inversely related: as wavelength decreases, energy increases (E = hc/λ)."
        },
        {
          id: "tdf-9",
          title: "Population Pyramid Data",
          context: "A population pyramid shows age distribution: 0-14 years = 28%, 15-29 years = 24%, 30-44 years = 20%, 45-59 years = 15%, 60-74 years = 9%, 75+ years = 4%. Males and females are roughly equal in each group.",
          data: "Description provided above.",
          questions: [
            "Create a table showing these age groups and percentages.",
            "What type of population structure is this?",
            "Graph this data (describe the shape)."
          ],
          solution: "1) Table created with Age Group and Percentage columns as given. 2) This is an expansive/growing population (wide base, narrow top). 3) Graph would show a pyramid shape with widest bar at bottom (0-14) tapering to narrow top (75+)."
        },
        {
          id: "tdf-10",
          title: "Reaction Energy Diagram",
          context: "An energy diagram shows: Reactants start at 50 kJ, rise to activation energy peak at 150 kJ, then drop to products at 20 kJ. With catalyst, the peak is only 90 kJ.",
          data: "Description provided above.",
          questions: [
            "Create a table showing energy values for reactants, activation energy (with/without catalyst), and products.",
            "Calculate the energy change (ΔH) for this reaction.",
            "Describe what the catalyst does."
          ],
          solution: "1) Table: Reactants=50kJ, Ea(no catalyst)=150kJ, Ea(with catalyst)=90kJ, Products=20kJ. 2) ΔH = 20-50 = -30 kJ (exothermic reaction). 3) The catalyst lowers activation energy from 150 to 90 kJ, making the reaction faster without changing ΔH."
        }
      ]
    },
    {
      id: "calculating-rates",
      title: "Calculating Rates of Change",
      description: "Determine how quickly variables change and calculate slopes, speeds, and rates",
      difficulty: "Intermediate",
      contentArea: "All Sciences",
      passages: [
        {
          id: "cr-1",
          title: "Chemical Reaction Rate",
          context: "A student monitored the concentration of a reactant over time during a chemical reaction.",
          data: "Table 1: Reactant Concentration\nTime (s) | [A] (mol/L)\n0 | 2.00\n10 | 1.65\n20 | 1.35\n30 | 1.10\n40 | 0.90\n50 | 0.73",
          questions: [
            "Calculate the average rate of reaction between 0-20 seconds.",
            "Calculate the average rate between 30-50 seconds.",
            "Is the reaction rate constant or changing?"
          ],
          solution: "1) Rate (0-20s) = (1.35-2.00)/(20-0) = -0.0325 mol/L/s (magnitude: 0.0325 mol/L/s). 2) Rate (30-50s) = (0.73-1.10)/(50-30) = -0.0185 mol/L/s (magnitude: 0.0185 mol/L/s). 3) Reaction rate is decreasing over time (slower at later times as reactant is depleted)."
        },
        {
          id: "cr-2",
          title: "Plant Growth Rate",
          context: "A botanist measured the height of a bamboo plant over one week.",
          data: "Figure 1: Bamboo Height\nDay | Height (cm)\n0 | 12\n1 | 27\n2 | 42\n3 | 57\n4 | 72\n5 | 87\n6 | 102\n7 | 117",
          questions: [
            "Calculate the growth rate in cm/day.",
            "How much did the plant grow in total?",
            "Is the growth rate constant?"
          ],
          solution: "1) Growth rate = (117-12)/(7-0) = 15 cm/day. 2) Total growth = 117-12 = 105 cm in 7 days. 3) Yes, growth rate is constant at 15 cm/day (linear relationship)."
        },
        {
          id: "cr-3",
          title: "Velocity from Position Data",
          context: "A motion sensor tracked the position of a cart on a track.",
          data: "Table 2: Cart Position\nTime (s) | Position (m)\n0.0 | 0.0\n0.5 | 1.0\n1.0 | 4.0\n1.5 | 9.0\n2.0 | 16.0\n2.5 | 25.0",
          questions: [
            "Calculate the average velocity between 0.5s and 1.5s.",
            "Calculate the average velocity between 1.5s and 2.5s.",
            "Is the cart moving at constant velocity?"
          ],
          solution: "1) Velocity (0.5-1.5s) = (9.0-1.0)/(1.5-0.5) = 8.0 m/s. 2) Velocity (1.5-2.5s) = (25.0-9.0)/(2.5-1.5) = 16.0 m/s. 3) No, velocity is increasing (cart is accelerating); position follows x = 4t²."
        },
        {
          id: "cr-4",
          title: "Cooling Rate of Metal",
          context: "A blacksmith measured the temperature of a hot iron bar as it cooled.",
          data: "Figure 2: Temperature vs. Time\nTime (min) | Temperature (°C)\n0 | 800\n2 | 620\n4 | 490\n6 | 390\n8 | 315\n10 | 260",
          questions: [
            "Calculate the cooling rate between 0-4 minutes.",
            "Calculate the cooling rate between 6-10 minutes.",
            "How does cooling rate change over time?"
          ],
          solution: "1) Cooling rate (0-4 min) = (490-800)/(4-0) = -77.5°C/min (magnitude: 77.5°C/min). 2) Cooling rate (6-10 min) = (260-390)/(10-6) = -32.5°C/min (magnitude: 32.5°C/min). 3) Cooling rate decreases over time (cools faster when temperature difference is larger)."
        },
        {
          id: "cr-5",
          title: "Bacterial Population Growth",
          context: "Microbiologists counted bacterial colonies at regular intervals.",
          data: "Table 3: Colony Count\nTime (hours) | Colonies\n0 | 100\n1 | 200\n2 | 400\n3 | 800\n4 | 1600\n5 | 3200",
          questions: [
            "Calculate the growth rate between hours 1 and 2.",
            "Calculate the growth rate between hours 4 and 5.",
            "What type of growth is this?"
          ],
          solution: "1) Growth rate (1-2h) = (400-200)/(2-1) = 200 colonies/hour. 2) Growth rate (4-5h) = (3200-1600)/(5-4) = 1600 colonies/hour. 3) This is exponential growth (population doubles every hour; rate increases over time)."
        },
        {
          id: "cr-6",
          title: "Glacier Melting Rate",
          context: "Scientists measured the volume of a glacier over several years.",
          data: "Figure 3: Glacier Volume\nYear | Volume (km³)\n2010 | 45.0\n2012 | 43.2\n2014 | 41.1\n2016 | 38.7\n2018 | 36.0\n2020 | 33.1",
          questions: [
            "Calculate the average melting rate over the entire period.",
            "Calculate the rate between 2010-2014 vs. 2016-2020.",
            "Is the melting rate accelerating?"
          ],
          solution: "1) Average rate (2010-2020) = (33.1-45.0)/(2020-2010) = -1.19 km³/year. 2) Rate (2010-2014) = (41.1-45.0)/4 = -0.975 km³/year; Rate (2016-2020) = (33.1-38.7)/4 = -1.4 km³/year. 3) Yes, melting rate is accelerating (faster loss in recent years)."
        },
        {
          id: "cr-7",
          title: "Photosynthesis Rate Measurement",
          context: "A student counted oxygen bubbles produced by an aquatic plant at different times.",
          data: "Table 4: Oxygen Production\nTime (min) | Total O₂ Bubbles\n0 | 0\n5 | 23\n10 | 47\n15 | 69\n20 | 92\n25 | 116",
          questions: [
            "Calculate the rate of O₂ production in bubbles/minute.",
            "How many bubbles were produced between 10-20 minutes?",
            "Is the rate constant?"
          ],
          solution: "1) Rate = (116-0)/(25-0) = 4.64 bubbles/min (approximately 4.6 bubbles/min). 2) Between 10-20 min: 92-47 = 45 bubbles produced. 3) Yes, rate is approximately constant (roughly 4.6 bubbles/min throughout)."
        },
        {
          id: "cr-8",
          title: "Acceleration from Velocity Data",
          context: "A car's velocity was recorded during acceleration.",
          data: "Figure 4: Velocity vs. Time\nTime (s) | Velocity (m/s)\n0 | 0\n2 | 8\n4 | 16\n6 | 24\n8 | 32\n10 | 40",
          questions: [
            "Calculate the acceleration (rate of velocity change).",
            "What will the velocity be at 12 seconds?",
            "How far does the car travel in the first 10 seconds?"
          ],
          solution: "1) Acceleration = (40-0)/(10-0) = 4 m/s². 2) At 12s, velocity = 0 + 4(12) = 48 m/s. 3) Distance = ½at² = ½(4)(10)² = 200 m."
        },
        {
          id: "cr-9",
          title: "Evaporation Rate",
          context: "A scientist measured the mass of water in an open container over time.",
          data: "Table 5: Water Mass\nTime (hours) | Mass (g)\n0 | 500\n6 | 482\n12 | 464\n18 | 446\n24 | 428\n30 | 410",
          questions: [
            "Calculate the evaporation rate in g/hour.",
            "How much water evaporated in 24 hours?",
            "When will the container be empty?"
          ],
          solution: "1) Evaporation rate = (410-500)/(30-0) = -3.0 g/hour (magnitude: 3.0 g/hour). 2) In 24 hours: 500-428 = 72 g evaporated. 3) Container will be empty in 500/3 ≈ 167 hours (about 7 days)."
        },
        {
          id: "cr-10",
          title: "Radioactive Decay Rate",
          context: "A sample of radioactive isotope was monitored over time.",
          data: "Figure 5: Activity (Becquerels)\nTime (days) | Activity (Bq)\n0 | 10000\n5 | 7071\n10 | 5000\n15 | 3536\n20 | 2500\n25 | 1768",
          questions: [
            "Calculate the average decay rate between 0-10 days.",
            "What is the half-life of this isotope?",
            "Is the decay rate constant?"
          ],
          solution: "1) Decay rate (0-10 days) = (5000-10000)/(10-0) = -500 Bq/day. 2) Half-life is 10 days (activity drops from 10000 to 5000 in 10 days). 3) No, the absolute decay rate decreases over time (exponential decay), but the fractional rate (half-life) is constant."
        }
      ]
    },
    {
      id: "comparing-data-sets",
      title: "Comparing Multiple Data Sets",
      description: "Analyze and compare data from multiple experiments, trials, or conditions",
      difficulty: "Advanced",
      contentArea: "All Sciences",
      passages: [
        {
          id: "cds-1",
          title: "Fertilizer Effects on Crop Yield",
          context: "Three different fertilizers were tested on corn plants. Yield was measured in bushels per acre.",
          data: "Table 1: Crop Yield by Fertilizer\nFertilizer | Plot 1 | Plot 2 | Plot 3 | Average\nControl (none) | 85 | 82 | 88 | 85\nFertilizer A | 112 | 118 | 115 | 115\nFertilizer B | 128 | 135 | 131 | 131\nFertilizer C | 119 | 122 | 117 | 119",
          questions: [
            "Which fertilizer produced the highest average yield?",
            "How much more effective is Fertilizer B than the control?",
            "Which fertilizer shows the most consistent results?"
          ],
          solution: "1) Fertilizer B produced the highest average yield at 131 bushels/acre. 2) Fertilizer B increased yield by 131-85 = 46 bushels/acre (54% increase). 3) Fertilizer A shows most consistent results (range of only 6 bushels across plots)."
        },
        {
          id: "cds-2",
          title: "Antibiotic Effectiveness Study",
          context: "Four antibiotics were tested against three bacterial strains. Zone of inhibition (mm) indicates effectiveness.",
          data: "Figure 1: Inhibition Zones\nAntibiotic | E. coli | S. aureus | P. aeruginosa\nPenicillin | 18 | 22 | 0\nTetracycline | 15 | 14 | 12\nCiprofloxacin | 28 | 25 | 24\nErythromycin | 12 | 19 | 8",
          questions: [
            "Which antibiotic is most effective overall?",
            "Which bacteria is most resistant to antibiotics?",
            "Which antibiotic is ineffective against P. aeruginosa?"
          ],
          solution: "1) Ciprofloxacin is most effective overall (largest zones for all three bacteria). 2) P. aeruginosa is most resistant (smallest average inhibition zones). 3) Penicillin is ineffective against P. aeruginosa (0 mm zone)."
        },
        {
          id: "cds-3",
          title: "Climate Data Comparison",
          context: "Average monthly temperatures were recorded for three cities at different latitudes.",
          data: "Table 2: Average Temperature (°C)\nMonth | Miami (26°N) | New York (41°N) | Anchorage (61°N)\nJan | 20 | 0 | -8\nApr | 24 | 11 | 2\nJul | 28 | 25 | 15\nOct | 26 | 14 | 2",
          questions: [
            "Which city has the greatest temperature variation?",
            "Which city has the warmest winter?",
            "How does latitude affect temperature patterns?"
          ],
          solution: "1) Anchorage has greatest variation (23°C range from -8 to 15). 2) Miami has the warmest winter (20°C in January). 3) Higher latitude cities have colder temperatures and greater seasonal variation; lower latitude cities are warmer and more stable year-round."
        },
        {
          id: "cds-4",
          title: "Enzyme Activity at Different pH Levels",
          context: "Three enzymes were tested at various pH levels to determine optimal conditions.",
          data: "Figure 2: Enzyme Activity (units/min)\npH | Pepsin | Amylase | Trypsin\n2 | 95 | 12 | 8\n4 | 58 | 35 | 22\n6 | 18 | 78 | 45\n8 | 5 | 82 | 88\n10 | 2 | 41 | 72",
          questions: [
            "What is the optimal pH for each enzyme?",
            "Which enzyme works best in acidic conditions?",
            "Which enzyme has the broadest pH tolerance?"
          ],
          solution: "1) Optimal pH: Pepsin at pH 2, Amylase at pH 8, Trypsin at pH 8. 2) Pepsin works best in acidic conditions (pH 2). 3) Trypsin has broadest tolerance (maintains >70% activity from pH 8-10, and >45 at pH 6)."
        },
        {
          id: "cds-5",
          title: "Fuel Efficiency Comparison",
          context: "Three vehicles were tested for fuel efficiency under different driving conditions.",
          data: "Table 3: Fuel Efficiency (mpg)\nCondition | Sedan | SUV | Hybrid\nCity | 28 | 18 | 42\nHighway | 38 | 24 | 48\nMountain | 22 | 16 | 38\nAverage | 29.3 | 19.3 | 42.7",
          questions: [
            "Which vehicle is most fuel-efficient overall?",
            "Which condition is hardest on fuel efficiency?",
            "Which vehicle shows the least variation across conditions?"
          ],
          solution: "1) Hybrid is most fuel-efficient overall (42.7 mpg average). 2) Mountain driving is hardest on efficiency (lowest values for all vehicles). 3) SUV shows least variation (range of 8 mpg vs. 16 for sedan and 10 for hybrid)."
        },
        {
          id: "cds-6",
          title: "Reaction Rates with Different Catalysts",
          context: "A chemical reaction was performed with three different catalysts at various temperatures.",
          data: "Figure 3: Reaction Rate (mol/L/s)\nTemp (°C) | No Catalyst | Catalyst A | Catalyst B\n20 | 0.012 | 0.045 | 0.089\n40 | 0.048 | 0.125 | 0.198\n60 | 0.152 | 0.312 | 0.445\n80 | 0.398 | 0.678 | 0.892",
          questions: [
            "Which catalyst is most effective?",
            "How much faster is Catalyst B than no catalyst at 60°C?",
            "Do catalysts change the temperature dependence?"
          ],
          solution: "1) Catalyst B is most effective (highest rates at all temperatures). 2) At 60°C, Catalyst B is 0.445/0.152 ≈ 2.9 times faster than no catalyst. 3) No, all show similar temperature dependence (rate roughly doubles every 20°C); catalysts increase rate but don't change activation energy pattern."
        },
        {
          id: "cds-7",
          title: "Species Diversity in Different Habitats",
          context: "Ecologists surveyed bird species in four habitat types over one year.",
          data: "Table 4: Species Count\nHabitat | Spring | Summer | Fall | Winter | Total Species\nForest | 45 | 52 | 48 | 38 | 68\nGrassland | 32 | 38 | 35 | 28 | 52\nWetland | 58 | 65 | 61 | 42 | 78\nUrban | 18 | 22 | 20 | 16 | 28",
          questions: [
            "Which habitat has the highest biodiversity?",
            "Which season has the most species overall?",
            "Which habitat shows the greatest seasonal variation?"
          ],
          solution: "1) Wetland has highest biodiversity (78 total species). 2) Summer has most species overall (highest counts in all habitats). 3) Wetland shows greatest seasonal variation (range of 23 species from winter to summer)."
        },
        {
          id: "cds-8",
          title: "Solar Panel Efficiency Comparison",
          context: "Three types of solar panels were tested under different light conditions.",
          data: "Figure 4: Power Output (Watts)\nCondition | Monocrystalline | Polycrystalline | Thin Film\nFull Sun | 285 | 265 | 220\nPartly Cloudy | 142 | 125 | 115\nOvercast | 48 | 38 | 52\nShade | 12 | 8 | 18",
          questions: [
            "Which panel performs best in full sun?",
            "Which panel performs best in low light?",
            "Which panel has the most consistent performance?"
          ],
          solution: "1) Monocrystalline performs best in full sun (285W). 2) Thin film performs best in low light (52W overcast, 18W shade). 3) Thin film has most consistent performance (maintains higher percentage of peak output in poor conditions)."
        },
        {
          id: "cds-9",
          title: "Water Quality in Three Rivers",
          context: "Environmental scientists measured pollutant levels at three river sites over four seasons.",
          data: "Table 5: Nitrate Concentration (mg/L)\nSeason | River A | River B | River C | Safe Limit\nSpring | 8.2 | 15.4 | 3.1 | 10.0\nSummer | 6.5 | 18.9 | 2.8 | 10.0\nFall | 9.8 | 22.3 | 4.2 | 10.0\nWinter | 11.2 | 12.8 | 3.5 | 10.0",
          questions: [
            "Which river consistently exceeds safe limits?",
            "Which river is cleanest?",
            "When is pollution highest overall?"
          ],
          solution: "1) River B consistently exceeds safe limits (all seasons above 10 mg/L). 2) River C is cleanest (all values well below 10 mg/L). 3) Pollution is highest in fall (highest average across all rivers, especially River B at 22.3)."
        },
        {
          id: "cds-10",
          title: "Drug Effectiveness Clinical Trial",
          context: "Three drugs were tested for reducing blood pressure. Results show average reduction in mmHg after 8 weeks.",
          data: "Figure 5: Blood Pressure Reduction\nDosage | Drug X | Drug Y | Drug Z | Placebo\nLow | 8 | 6 | 10 | 2\nMedium | 15 | 12 | 18 | 2\nHigh | 22 | 16 | 24 | 2",
          questions: [
            "Which drug is most effective at medium dosage?",
            "Which drug shows the greatest dose-response relationship?",
            "How much better is Drug Z (high dose) than placebo?"
          ],
          solution: "1) Drug Z is most effective at medium dosage (18 mmHg reduction). 2) Drug X shows greatest dose-response (increases from 8→15→22, nearly linear). 3) Drug Z (high dose) is 24-2 = 22 mmHg better than placebo (12 times more effective)."
        }
      ]
    }
  ]
};